import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  Grid, Card, CardContent, Divider, Table, TableBody,
  TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Stack, Paper, IconButton, Tooltip
} from '@mui/material';
import {
  ArrowBack, Email, Add, CheckCircle, Warning, EventBusy, Edit, Save, Cancel as CancelIcon
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosConfig';
import { useAuth } from '../../../contexts/AuthContext';

interface TILEntry {
  _id: string;
  hours: number;
  hoursUsed?: number;
  earnedDate: string;
  expiryDate: string;
  takenDate?: string;
  description: string;
  status?: 'active' | 'taken' | 'expired' | 'partial';
}

interface StaffLeave {
  _id: string;
  name: string;
  email: string;
  leaveEntitlement?: {
    annualLeaveTotal: number;
    annualLeaveCurrentYear?: number;
    annualLeaveUsed: number;
    annualLeaveCarryover?: number;
    annualLeaveCarryoverExpiry?: string;
    timeInLieuHours: number;
    timeInLieuUsed: number;
    timeInLieuEntries: TILEntry[];
  };
}

const NOTIFY_TEMPLATES = {
  'al-reminder': {
    label: 'Annual Leave Reminder',
    type: 'annual-leave',
    subject: 'Reminder — Annual Leave Balance',
    body: (name: string, remaining: number) =>
      `Hi ${name},\n\nThis is a friendly reminder that you currently have ${remaining} day${remaining !== 1 ? 's' : ''} of annual leave remaining for this year.\n\nPlease make sure to book your remaining leave before the end of December. Unused days carry over until 31 January only.\n\nIf you need help booking time off, just get in touch.`
  },
  'til-expiry': {
    label: 'TIL Expiry Warning',
    type: 'time-lieu',
    subject: 'Time in Lieu — Expiry Notice',
    body: (name: string) =>
      `Hi ${name},\n\nThis is a reminder that you have Time in Lieu hours that are approaching their expiry date.\n\nPlease arrange to take this time off as soon as possible — unused TIL cannot be carried forward beyond its expiry date.\n\nIf you'd like to discuss scheduling, please get in touch.`
  },
  'custom': {
    label: 'Custom Message',
    type: 'annual-leave',
    subject: '',
    body: () => ''
  }
};

const StaffLeaveProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [staff, setStaff] = useState<StaffLeave | null>(null);
  const [alHistory, setAlHistory] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [emailOpen, setEmailOpen] = useState(false);
  const [template, setTemplate] = useState<keyof typeof NOTIFY_TEMPLATES>('al-reminder');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [addTILOpen, setAddTILOpen] = useState(false);
  const [tilHours, setTilHours] = useState('');
  const [tilDesc, setTilDesc] = useState('');
  const [addingTIL, setAddingTIL] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin';
  const [editingContractual, setEditingContractual] = useState(false);
  const [contractualValue, setContractualValue] = useState(0);

  const handleSaveContractual = async () => {
    if (!id) return;
    try {
      await axiosInstance.patch(`/api/personnel/${id}`, {
        'leaveEntitlement.annualLeaveTotal': contractualValue
      });
      setEditingContractual(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update entitlement');
    }
  };

  useEffect(() => { if (id) fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [staffRes, leaveReqRes] = await Promise.all([
        axiosInstance.get(`/api/personnel/${id}`),
        axiosInstance.get('/api/leave-requests')
      ]);
      setStaff(staffRes.data);

      const year = new Date().getFullYear();
      const approved = leaveReqRes.data.filter(
        (r: any) =>
          r.personnelId?._id === id &&
          r.type === 'annual-leave' &&
          r.status === 'approved' &&
          new Date(r.dateStart).getFullYear() === year
      );
      const days: string[] = [];
      approved.forEach((r: any) => {
        const d = new Date(r.dateStart);
        days.push(d.toISOString());
      });
      days.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
      setAlHistory(days);
    } catch (err: any) {
      setError('Failed to load staff leave data');
    } finally {
      setLoading(false);
    }
  };

  const openEmail = (t: keyof typeof NOTIFY_TEMPLATES) => {
    setTemplate(t);
    const tmpl = NOTIFY_TEMPLATES[t];
    const remaining = alRemaining;
    setEmailSubject(tmpl.subject);
    setEmailBody(t === 'al-reminder'
      ? NOTIFY_TEMPLATES['al-reminder'].body(staff?.name || '', remaining)
      : t === 'til-expiry'
        ? NOTIFY_TEMPLATES['til-expiry'].body(staff?.name || '')
        : '');
    setSent(false);
    setEmailOpen(true);
  };

  const handleSendEmail = async () => {
    if (!id) return;
    setSending(true);
    try {
      await axiosInstance.post(`/api/personnel/${id}/leave/notify`, {
        type: NOTIFY_TEMPLATES[template].type,
        subject: emailSubject,
        message: emailBody
      });
      setSent(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleAddTIL = async () => {
    if (!id || !tilHours) return;
    setAddingTIL(true);
    try {
      await axiosInstance.post(`/api/personnel/${id}/leave/add-til`, {
        hours: parseFloat(tilHours),
        description: tilDesc || 'Manual TIL adjustment',
        adminOverride: true
      });
      setAddTILOpen(false);
      setTilHours('');
      setTilDesc('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add TIL');
    } finally {
      setAddingTIL(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  if (!staff) return <Box sx={{ p: 3 }}><Alert severity="error">Staff member not found</Alert></Box>;

  const ent = staff.leaveEntitlement;
  const contractual = ent?.annualLeaveTotal || 0;
  const currentYear = ent?.annualLeaveCurrentYear || contractual;
  const used = ent?.annualLeaveUsed || 0;
  const alRemaining = currentYear - used;
  const carryover = ent?.annualLeaveCarryover || 0;
  const carryoverExpiry = ent?.annualLeaveCarryoverExpiry ? new Date(ent.annualLeaveCarryoverExpiry) : null;
  const carryoverActive = carryover > 0 && carryoverExpiry && carryoverExpiry > new Date();

  const tilTotal = ent?.timeInLieuHours || 0;
  const tilUsed = ent?.timeInLieuUsed || 0;
  const tilBalance = parseFloat((tilTotal - tilUsed).toFixed(2));
  const tilEntries = ent?.timeInLieuEntries || [];

  const now = new Date();
  const soon = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const expiringTIL = tilEntries.filter(e => {
    const exp = new Date(e.expiryDate);
    return exp >= now && exp <= soon;
  });

  const alPct = currentYear > 0 ? Math.round((used / currentYear) * 100) : 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/leave-management')}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">{staff.name}</Typography>
          <Typography variant="body2" color="text.secondary">{staff.email}</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Email />} onClick={() => openEmail('al-reminder')}>
          Email about AL
        </Button>
        <Button variant="outlined" color="warning" startIcon={<Email />} onClick={() => openEmail('til-expiry')}>
          Email about TIL
        </Button>
        <Button variant="outlined" startIcon={<Email />} onClick={() => openEmail('custom')}>
          Custom Message
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => setAddTILOpen(true)}>
          Add TIL
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Balance Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Annual Leave */}
        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="overline" color="text.secondary">Annual Leave</Typography>
                {isSuperAdmin && !editingContractual && (
                  <Tooltip title="Edit contractual entitlement">
                    <IconButton size="small" onClick={() => { setContractualValue(contractual); setEditingContractual(true); }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="h2" fontWeight="bold" color={alRemaining <= 2 ? 'error.main' : alRemaining <= 5 ? 'warning.main' : 'success.main'}>
                {alRemaining}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                days remaining of <strong>{currentYear}</strong> total
              </Typography>
              <Typography variant="caption" color="text.secondary">{used} used</Typography>
              <Box sx={{ mt: 1.5, mb: 0.5, height: 6, bgcolor: 'grey.200', borderRadius: 3 }}>
                <Box sx={{ width: `${Math.min(alPct, 100)}%`, height: '100%', bgcolor: alPct > 90 ? 'error.main' : 'primary.main', borderRadius: 3 }} />
              </Box>
              <Typography variant="caption" color="text.secondary">{alPct}% used</Typography>
              {isSuperAdmin && editingContractual && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Contractual entitlement (days/year)
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      type="number"
                      value={contractualValue}
                      onChange={e => setContractualValue(Number(e.target.value))}
                      inputProps={{ min: 0, max: 30 }}
                      sx={{ width: 80 }}
                    />
                    <IconButton size="small" color="success" onClick={handleSaveContractual}>
                      <Save fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setEditingContractual(false)}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Carryover */}
        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ height: '100%', border: carryoverActive ? '2px solid #ed6c02' : '2px solid transparent' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Carry-Over Days</Typography>
              <Typography variant="h2" fontWeight="bold" color={carryoverActive ? 'warning.main' : 'text.disabled'}>
                {carryover}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {carryoverActive
                  ? `Expires ${carryoverExpiry!.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
                  : carryover > 0 ? 'Expired' : 'None this year'}
              </Typography>
              {carryoverActive && (
                <Chip label="Use before Jan 31" color="warning" size="small" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* TIL */}
        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ height: '100%', border: expiringTIL.length > 0 ? '2px solid #7c3aed' : '2px solid transparent' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Time in Lieu</Typography>
              <Typography variant="h2" fontWeight="bold" color={tilBalance > 16 ? 'error.main' : 'secondary.main'}>
                {tilBalance}h
              </Typography>
              <Typography variant="body2" color="text.secondary">{tilUsed}h used of {tilTotal}h earned</Typography>
              {expiringTIL.length > 0 && (
                <Chip
                  icon={<Warning />}
                  label={`${expiringTIL.length} entr${expiringTIL.length === 1 ? 'y' : 'ies'} expiring soon`}
                  color="warning"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
              {tilBalance > 16 && (
                <Chip label="Exceeds 16h guideline" color="error" size="small" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* AL History */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Annual Leave Taken — {new Date().getFullYear()}</Typography>
              {alHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No annual leave taken this year</Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Day</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alHistory.map((d, i) => {
                        const date = new Date(d);
                        const monthParam = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                        return (
                          <TableRow key={i}>
                            <TableCell>{date.toLocaleDateString('en-GB')}</TableCell>
                            <TableCell>{date.toLocaleDateString('en-GB', { weekday: 'long' })}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Verify on rota">
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ fontSize: 11, py: 0, minWidth: 0, color: 'text.secondary' }}
                                  onClick={() => navigate(`/admin/rota?month=${monthParam}`)}
                                >
                                  Rota ↗
                                </Button>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell colSpan={3} align="right">
                          <strong>Total: {alHistory.length} day{alHistory.length !== 1 ? 's' : ''}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* TIL Entries */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>TIL Entries</Typography>
              {tilEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No TIL entries</Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Earned</TableCell>
                        <TableCell>Hours</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tilEntries.map((e) => {
                        const expiry = new Date(e.expiryDate);
                        const expired = expiry < now;
                        const expiringSoon = !expired && expiry <= soon;
                        const hoursUsed = e.hoursUsed || 0;
                        const hoursRemaining = e.hours - hoursUsed;

                        let statusChip;
                        if (e.status === 'taken' || hoursRemaining <= 0) {
                          statusChip = <Chip icon={<CheckCircle />} label="Taken" color="success" size="small" />;
                        } else if (e.status === 'partial') {
                          statusChip = <Chip icon={<CheckCircle />} label={`${hoursUsed}h taken`} color="info" size="small" />;
                        } else if (expired) {
                          statusChip = <Chip label="Expired" color="error" size="small" />;
                        } else if (expiringSoon) {
                          statusChip = <Chip icon={<Warning />} label="Expiring soon" color="warning" size="small" />;
                        } else {
                          statusChip = <Chip icon={<CheckCircle />} label="Active" color="success" size="small" />;
                        }

                        return (
                          <TableRow key={e._id}>
                            <TableCell>{new Date(e.earnedDate).toLocaleDateString('en-GB')}</TableCell>
                            <TableCell>
                              <strong>{e.hours}h</strong>
                              {hoursUsed > 0 && hoursRemaining > 0 && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {hoursRemaining}h remaining
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{expiry.toLocaleDateString('en-GB')}</TableCell>
                            <TableCell>{statusChip}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Email Dialog */}
      <Dialog open={emailOpen} onClose={() => !sending && setEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {sent ? 'Email Sent' : `Email ${staff.name}`}
        </DialogTitle>
        <DialogContent>
          {sent ? (
            <Alert severity="success" icon={<CheckCircle />}>
              Email sent to {staff.email} and logged in the audit trail.
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Template</InputLabel>
                <Select
                  value={template}
                  label="Template"
                  onChange={(e) => openEmail(e.target.value as keyof typeof NOTIFY_TEMPLATES)}
                >
                  {Object.entries(NOTIFY_TEMPLATES).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Subject"
                fullWidth
                size="small"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
              <TextField
                label="Message"
                fullWidth
                multiline
                rows={7}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
              <Typography variant="caption" color="text.secondary">
                Sending to: {staff.email} · Will be logged in audit trail
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOpen(false)} disabled={sending}>Close</Button>
          {!sent && (
            <Button
              variant="contained"
              startIcon={<Email />}
              onClick={handleSendEmail}
              disabled={sending || !emailSubject || !emailBody}
            >
              {sending ? 'Sending…' : 'Send Email'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add TIL Dialog */}
      <Dialog open={addTILOpen} onClose={() => !addingTIL && setAddTILOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add TIL — {staff.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Hours"
              type="number"
              fullWidth
              size="small"
              value={tilHours}
              onChange={(e) => setTilHours(e.target.value)}
              inputProps={{ min: 0.5, step: 0.5 }}
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              value={tilDesc}
              onChange={(e) => setTilDesc(e.target.value)}
              placeholder="e.g. Weekend event overtime"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTILOpen(false)} disabled={addingTIL}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTIL} disabled={addingTIL || !tilHours}>
            {addingTIL ? 'Adding…' : 'Add TIL'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffLeaveProfilePage;
